import { PERIODS, type Period, type PeriodKey } from './resolution'

/**
 * Per-indicator timeframe visibility (the indicator settings "Visibility" tab).
 *
 * A rule maps period keys to a visible flag: a period that is absent (or `true`)
 * is visible, `false` hides the indicator on that timeframe. A `undefined` rule
 * means visible on every timeframe.
 *
 * The effective on-chart visibility a caller applies is:
 *   eyeVisible (manual toggle)  &&  evaluateTimeframeVisibility(rule, period)
 */

export type TimeframeUnit = Period['timespan']

export type TimeframeVisibilityRule = Partial<Record<PeriodKey, boolean>>

/** Visibility-tab grouping: three coarse buckets rather than one per unit, so
 *  the daily-and-up periods (1D/1W/1M/1Y) sit together as one "Daily" row. */
export type TimeframeCategory = 'intraday' | 'hourly' | 'daily'

export interface TimeframeCategoryGroup {
  category: TimeframeCategory
  /** Group heading in the Visibility tab. */
  label: string
  periods: { key: PeriodKey; label: string }[]
}

const CATEGORY_LABEL: Record<TimeframeCategory, string> = {
  intraday: 'Intraday',
  hourly: 'Hourly',
  daily: 'Daily',
}
const CATEGORY_OF_UNIT: Record<TimeframeUnit, TimeframeCategory> = {
  minute: 'intraday',
  hour: 'hourly',
  day: 'daily',
  week: 'daily',
  month: 'daily',
  year: 'daily',
}
const CATEGORY_ORDER: TimeframeCategory[] = ['intraday', 'hourly', 'daily']
const UNIT_ORDER: TimeframeUnit[] = ['minute', 'hour', 'day', 'week', 'month', 'year']

/**
 * Every period grouped into the three Visibility-tab categories, ordered
 * smallest → largest within each. Derived from PERIODS so it stays in sync as
 * timeframes are added.
 */
export const TIMEFRAME_CATEGORY_GROUPS: readonly TimeframeCategoryGroup[] = CATEGORY_ORDER.map(
  (category) => ({
    category,
    label: CATEGORY_LABEL[category],
    periods: UNIT_ORDER.filter((unit) => CATEGORY_OF_UNIT[unit] === category).flatMap((unit) =>
      (Object.keys(PERIODS) as PeriodKey[])
        .filter((k) => PERIODS[k].timespan === unit)
        .map((k) => ({ key: k, label: PERIODS[k].label })),
    ),
  }),
).filter((g) => g.periods.length > 0)

/** Every period key, in Visibility-tab order — the "All on / All off" domain. */
export const ALL_PERIOD_KEYS: readonly PeriodKey[] = TIMEFRAME_CATEGORY_GROUPS.flatMap((g) =>
  g.periods.map((p) => p.key),
)

/** Every daily-and-higher period key (1D/1W/1M), derived from PERIODS so a
 *  future coarse timeframe is covered automatically. */
const DAILY_AND_UP_KEYS = (Object.keys(PERIODS) as PeriodKey[]).filter((k) =>
  (['day', 'week', 'month', 'year'] as TimeframeUnit[]).includes(PERIODS[k].timespan),
)

/**
 * Built-in (catalog-level) default timeframe rules, applied when an indicator
 * entry carries no explicit `timeframeVisibility` of its own. An explicit rule
 * on the entry — user-saved default or Visibility-tab edit — always wins.
 *
 * VWAP: hidden on every daily-and-higher timeframe. VWAP is session-anchored —
 * it resets at each session open and averages intraday trades — so on daily+
 * bars (one bar per session or more) there is no session to anchor to and the
 * plotted line is meaningless noise hugging the closes.
 *
 * VPXD: same reasoning. "The last 2 days" of daily bars is two bars, which bins
 * into a profile with no intraday shape to show, and the midnight-vs-prior-close
 * anchor has no meaning when a bar IS a day.
 *
 * HTF: its targets are intraday periods, so on a daily-or-coarser chart there is
 * no HIGHER intraday timeframe to project — every target would be at or below the
 * chart's own interval and the projection would be empty.
 */
export const BUILT_IN_TIMEFRAME_VISIBILITY: Record<string, TimeframeVisibilityRule> = {
  VWAP: Object.fromEntries(DAILY_AND_UP_KEYS.map((k) => [k, false])),
  VPXD: Object.fromEntries(DAILY_AND_UP_KEYS.map((k) => [k, false])),
  HTF: Object.fromEntries(DAILY_AND_UP_KEYS.map((k) => [k, false])),
}

/** Whether an indicator with `rule` should draw on `period`. */
export function evaluateTimeframeVisibility(
  rule: TimeframeVisibilityRule | undefined,
  period: Period,
): boolean {
  if (!rule) return true
  // Period labels match the PERIODS keys (e.g. '5m', '1D').
  return rule[period.label as PeriodKey] !== false
}

/** True when the rule imposes no restriction (every period visible). */
export function isTimeframeVisibilityUnrestricted(
  rule: TimeframeVisibilityRule | undefined,
): boolean {
  if (!rule) return true
  return Object.values(rule).every((v) => v !== false)
}
